import { useCallback, useEffect, useMemo, useState } from "react";
import { App } from "antd";
import { trpc } from "../../trpc";
import { filterRoleColumnMerges } from "../../../shared/feishuSheetMerges";
import {
  collectScriptSheetImages,
  filterScriptSheetRows,
  hasScriptSheetContentInRow,
  isScriptAtmosphereRole,
  parseScriptSheetValues,
  type ParsedScriptSheetRow,
  type ScriptSheetImageRef,
} from "../../../shared/feishuScriptSheet";
import {
  SCRIPT_CONTENT_COLUMN,
  buildAssignmentRowEligibility,
  buildScriptRoleBlockSummaries,
  collectAtmosphereContentImageTokens,
} from "../../../shared/feishuScriptAssignment";
import type { ImageUnderstandingState, SheetReadResult } from "./types";

const OVERWRITE_MODE = false;

export function useScriptSheetData() {
  const { message } = App.useApp();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [credentialsLoading, setCredentialsLoading] = useState(true);
  const [hasCredentials, setHasCredentials] = useState(false);
  const [hasImageUnderstandingModel, setHasImageUnderstandingModel] = useState(false);
  const [result, setResult] = useState<SheetReadResult | null>(null);
  const [groups, setGroups] = useState<string[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<string | undefined>();
  const [memberCount, setMemberCount] = useState(0);
  const [membersLoading, setMembersLoading] = useState(false);
  const [imageUnderstandings, setImageUnderstandings] = useState<
    Record<string, ImageUnderstandingState>
  >({});

  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      let data = await trpc.atmosphereMember.groups.query();
      if (!data.length) {
        const members = await trpc.atmosphereMember.list.query({});
        data = [
          ...new Set(
            members
              .map((member) => member.group?.trim())
              .filter((group): group is string => Boolean(group))
          ),
        ].sort((a, b) => a.localeCompare(b, "zh-CN"));
      }
      setGroups(data);
    } catch (error: unknown) {
      setGroups([]);
      message.error(error instanceof Error ? error.message : "加载气氛员分组失败");
    } finally {
      setGroupsLoading(false);
    }
  }, [message]);

  useEffect(() => {
    trpc.setting
      .getFeishuApiSettings.query()
      .then((settings) => {
        setHasCredentials(Boolean(settings.appId && settings.hasAppSecret));
      })
      .catch(() => setHasCredentials(false))
      .finally(() => setCredentialsLoading(false));

    trpc.aiModel
      .getImageUnderstandingStatus.query()
      .then((status) => setHasImageUnderstandingModel(Boolean(status.modelId)))
      .catch(() => setHasImageUnderstandingModel(false));

    void loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    if (!selectedGroup) {
      setMemberCount(0);
      return;
    }

    let cancelled = false;
    setMembersLoading(true);
    void trpc.atmosphereMember.list
      .query({ group: selectedGroup })
      .then((members) => {
        if (!cancelled) setMemberCount(members.length);
      })
      .catch(() => {
        if (!cancelled) setMemberCount(0);
      })
      .finally(() => {
        if (!cancelled) setMembersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedGroup]);

  const handleRead = useCallback(async () => {
    if (!selectedGroup) {
      message.warning("请先选择气氛员分组");
      return;
    }
    if (!url.trim()) {
      message.warning("请输入飞书表格链接");
      return;
    }

    setLoading(true);
    try {
      const data = await trpc.feishuSheets.readByUrl.mutate({ url: url.trim() });
      setResult(data);
      void loadGroups();
      message.success("读取成功");
      return true;
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : "读取失败");
      return false;
    } finally {
      setLoading(false);
    }
  }, [url, selectedGroup, loadGroups, message]);

  const { parsedRows, allParsedRows, scriptRoleBlocks } = useMemo(() => {
    if (!result?.values.length) {
      return { parsedRows: [], allParsedRows: [], scriptRoleBlocks: [] };
    }
    const merges = filterRoleColumnMerges(result.roleColumnMerges ?? []);
    const allRows = parseScriptSheetValues(result.values, result.rawValues, merges);
    return {
      allParsedRows: allRows,
      parsedRows: filterScriptSheetRows(allRows),
      scriptRoleBlocks: buildScriptRoleBlockSummaries(allRows, merges, {
        overwriteMode: OVERWRITE_MODE,
      }),
    };
  }, [result]);

  const rowEligibility = useMemo(
    () => buildAssignmentRowEligibility(allParsedRows, { overwriteMode: OVERWRITE_MODE }),
    [allParsedRows]
  );

  const atmosphereRows = useMemo(
    () => parsedRows.filter((row) => isScriptAtmosphereRole(row.role)),
    [parsedRows]
  );

  const messageCount = useMemo(
    () => atmosphereRows.filter((row) => hasScriptSheetContentInRow(row)).length,
    [atmosphereRows]
  );

  const eligibleRoleCount = useMemo(() => {
    const roleIds = new Set<string>();
    for (const row of allParsedRows) {
      if (!isScriptAtmosphereRole(row.role)) continue;
      const info = rowEligibility.get(row.rowIndex);
      if (info?.eligible && info.tempRoleId) {
        roleIds.add(info.tempRoleId);
      }
    }
    return roleIds.size;
  }, [allParsedRows, rowEligibility]);

  const assignmentContentImages = useMemo(() => {
    if (!result?.rawValues?.length) return [] as ScriptSheetImageRef[];
    const requiredTokens = new Set(
      collectAtmosphereContentImageTokens(parsedRows, { overwriteMode: OVERWRITE_MODE })
    );
    if (!requiredTokens.size) return [] as ScriptSheetImageRef[];
    return collectScriptSheetImages(parsedRows, result.rawValues).filter(
      (item) => item.column === SCRIPT_CONTENT_COLUMN && requiredTokens.has(item.fileToken)
    );
  }, [parsedRows, result]);

  useEffect(() => {
    if (!result || assignmentContentImages.length === 0) return;

    const fileTokens = assignmentContentImages.map((item) => item.fileToken);
    let cancelled = false;
    void trpc.feishuSheets
      .getImageUnderstandingCache.query({ fileTokens })
      .then((cache) => {
        if (cancelled) return;
        setImageUnderstandings((prev) => {
          const next = { ...prev };
          for (const [fileToken, item] of Object.entries(cache.items)) {
            if (!item.content?.trim()) continue;
            next[fileToken] = {
              content: item.content,
              cached: true,
              understoodAt: item.understoodAt,
            };
          }
          return next;
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [result, assignmentContentImages]);

  const buildImageTextsPayload = useCallback(() => {
    const payload: Record<string, string> = {};
    for (const [fileToken, item] of Object.entries(imageUnderstandings)) {
      if (item.content?.trim()) {
        payload[fileToken] = item.content.trim();
      }
    }
    return payload;
  }, [imageUnderstandings]);

  const resetSheet = useCallback(() => {
    setResult(null);
    setImageUnderstandings({});
  }, []);

  return {
    url,
    setUrl,
    loading,
    credentialsLoading,
    hasCredentials,
    hasImageUnderstandingModel,
    result,
    groups,
    groupsLoading,
    selectedGroup,
    setSelectedGroup,
    memberCount,
    membersLoading,
    handleRead,
    parsedRows,
    allParsedRows,
    atmosphereRows,
    scriptRoleBlocks,
    rowEligibility,
    messageCount,
    eligibleRoleCount,
    assignmentContentImages,
    imageUnderstandings,
    setImageUnderstandings,
    buildImageTextsPayload,
    resetSheet,
    overwriteMode: OVERWRITE_MODE,
  };
}

export type ScriptSheetData = ReturnType<typeof useScriptSheetData>;
